import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ServicemanagementService } from '../../../services/servicemanagement.service';
import { ServiceRow } from '../../../models/servicemanagement.model';
import { map, Observable, startWith } from 'rxjs';
import { AddServiceDialogData, AddServiceDialogResult } from '../../../models/addservicedialog.model';
import { toggleAutocompletePanel } from '../../../shared/utils/autocomplete-panel.utils';

const MEDEXCLD_SERVICE = 'MEDEXCLD';
const QUERYALL_SERVICE = 'QUERYALL';

type AddServiceForm = FormGroup<{
  serviceName: FormControl<string>;
  startDate: FormControl<Date | null>;
}>;

@Component({
  selector: 'app-addservicedialog',
  standalone: false,
  templateUrl: './addservicedialog.component.html',
  styleUrls: ['./addservicedialog.component.css']
})
export class AddservicedialogComponent implements OnInit {
  @Output() submitRequest = new EventEmitter<AddServiceDialogResult>();
  @ViewChild('serviceTrigger', { read: MatAutocompleteTrigger }) serviceAutocompleteTrigger?: MatAutocompleteTrigger;
  form: AddServiceForm;
  serviceOptions: ServiceRow[] = [];
  filteredServiceOptions$: Observable<ServiceRow[]> = new Observable<ServiceRow[]>();
  isLoading = false;
  errorMessage = '';
  selectedServiceDescription = '';
  private hasUserInteractedWithServiceInput = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddservicedialogComponent, AddServiceDialogResult>,
    private serviceManagementService: ServicemanagementService,
    @Inject(MAT_DIALOG_DATA) public data: AddServiceDialogData
  ) {
    this.form = this.fb.group({
      serviceName: this.fb.control<string>('', { validators: [Validators.required, Validators.maxLength(200), this.serviceOptionValidator.bind(this)], nonNullable: true }),
      startDate: this.fb.control<Date | null>(new Date())
    });

    this.form.get('serviceName')?.valueChanges.subscribe((serviceName) => {
      this.updateSelectedServiceDescription(serviceName);
    });

    this.configureFilteredServiceOptions(this.form.controls.serviceName.value);
  }

  ngOnInit(): void {
    this.loadUnassignedServices();
  }

  private loadUnassignedServices(): void {
    if (!this.data?.clientId) return;
    this.isLoading = true;
    this.errorMessage = '';
    this.serviceManagementService.getUnassignedServices(this.data.clientId)
      .pipe(
        map(response => {
          if (Array.isArray(response)) {
            return response;
          }
          return (response as any)?.services ?? [];
        })
      )
      .subscribe({
        next: (services: ServiceRow[]) => {
          this.serviceOptions = this.filterServiceOptions(services ?? []);
          const currentServiceName = this.form.controls.serviceName.value;
          this.form.controls.serviceName.updateValueAndValidity({ emitEvent: false });
          this.updateSelectedServiceDescription(currentServiceName);
          this.configureFilteredServiceOptions(currentServiceName);

          setTimeout(() => {
            if (!this.hasUserInteractedWithServiceInput) {
              this.serviceAutocompleteTrigger?.closePanel();
            }
          }, 0);

          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage = `Failed to load services${err?.status ? ` (HTTP ${err.status})` : ''}.`;
          this.isLoading = false;
        }
      });
  }

  submit(): void {
    if (this.form.invalid) return;
    const v = this.form.getRawValue();
    this.errorMessage = '';
    this.submitRequest.emit({
      serviceName: v.serviceName,
      startDate: v.startDate
    });
  }

  setDialogError(message: string): void {
    this.errorMessage = message;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  clearSelectedService(event: Event): void {
    event.stopPropagation();
    this.form.controls.serviceName.setValue('');
  }

  markServiceInputInteracted(): void {
    this.hasUserInteractedWithServiceInput = true;
  }

  openOrClosePanel(event: Event, trigger: MatAutocompleteTrigger): void {
    this.markServiceInputInteracted();
    toggleAutocompletePanel(event, trigger);
  }

  private updateSelectedServiceDescription(serviceName: string): void {
    const selected = this.serviceOptions.find(s => s.ServiceName === serviceName);
    this.selectedServiceDescription = selected?.ServiceDescription ?? '';
    this.adjustTextareaHeight();
  }

  private filterServicesByName(serviceName: string): ServiceRow[] {
    const filterValue = (serviceName ?? '').trim().toLowerCase();

    if (!filterValue) {
      return this.serviceOptions.slice();
    }

    return this.serviceOptions.filter((serviceOption) =>
      (serviceOption.ServiceName ?? '').toLowerCase().includes(filterValue)
    );
  }

  private configureFilteredServiceOptions(initialValue: string): void {
    this.filteredServiceOptions$ = this.form.controls.serviceName.valueChanges.pipe(
      startWith(initialValue),
      map((serviceName) => this.filterServicesByName(serviceName))
    );
  }

  private serviceOptionValidator(control: AbstractControl<string>): ValidationErrors | null {
    const value = (control.value ?? '').trim();

    if (!value) {
      return null;
    }

    return this.serviceOptions.some((serviceOption) => serviceOption.ServiceName === value)
      ? null
      : { invalidOption: true };
  }

  private filterServiceOptions(serviceOptions: ServiceRow[]): ServiceRow[] {
    const existingServices = new Set(
      (this.data?.existingServiceNames ?? [])
        .map((serviceName) => (serviceName ?? '').trim().toUpperCase())
        .filter((serviceName) => !!serviceName)
    );

    return serviceOptions.filter((serviceOption) => {
      const serviceName = (serviceOption.ServiceName ?? '').trim().toUpperCase();

      if (serviceName === MEDEXCLD_SERVICE) {
        return !existingServices.has(QUERYALL_SERVICE);
      }

      if (serviceName === QUERYALL_SERVICE) {
        return !existingServices.has(MEDEXCLD_SERVICE);
      }

      return true;
    });
  }

  private adjustTextareaHeight(): void {
    setTimeout(() => {
      const textarea = document.querySelector('.auto-resize-textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    }, 0);
  }
}